/**
 * Admin → Bulk Customer Import
 *
 * Workflow:
 *   1. Admin downloads the Excel template.
 *   2. Team fills it in offline with all customer + pet + subscription data.
 *   3. Admin uploads the filled template here.
 *   4. Backend creates users, addresses, pets, subscriptions.
 *   5. Each new customer receives a magic-link email + WhatsApp to set
 *      their password and confirm the plan in their cabinet.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Loader2,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Users,
  Mail,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminBulkImport() {
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = useCallback(async () => {
    setDownloading(true);
    try {
      const t = getToken();
      const res = await fetch(`${API}/api/admin/bulk-import/template`, {
        headers: { Authorization: t ? `Bearer ${t}` : '' },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'smartpaw_bulk_import_template.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download');
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please upload the .xlsx template');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const t = getToken();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/admin/bulk-import`, {
        method: 'POST',
        headers: { Authorization: t ? `Bearer ${t}` : '' },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Import failed' }));
        throw new Error(err.detail || 'Import failed');
      }
      const data = await res.json();
      setResult(data);
      toast.success(
        `Imported ${data.customers_created} new customer(s), ${data.rows_processed} row(s).`,
      );
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-6" data-testid="admin-bulk-import-page">
      <header>
        <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Operations · Bulk import</p>
        <h1 className="font-display text-3xl text-[#05223D]">Bulk customer import</h1>
        <p className="text-sm text-[#465B70] mt-1 max-w-2xl">
          Register a batch of existing customers in one go. Download the template, fill it in with your team, and upload it back — SmartPaw will create each customer, prepare their plan, and email them a secure link to set their password and confirm the details.
        </p>
      </header>

      {/* Step 1 — Download template */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F25C05]/12 text-[#F25C05] font-bold">1</div>
            <div className="flex-1">
              <h2 className="font-bold text-[#05223D]">Download the template</h2>
              <p className="text-sm text-[#465B70] mt-1 mb-3">One Excel row = one delivery line. Repeat the customer’s email for each additional product; blank cells inherit values from the customer’s first row.</p>
              <Button onClick={downloadTemplate} disabled={downloading} variant="outline" className="h-10 rounded-xl" data-testid="bulk-import-download-template">
                {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download Excel template
              </Button>
            </div>
            <FileSpreadsheet className="h-8 w-8 text-[#0A4D8C]/40 hidden sm:block" />
          </div>
        </CardContent>
      </Card>

      {/* Step 2 — Upload */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0A4D8C]/12 text-[#0A4D8C] font-bold">2</div>
            <div className="flex-1">
              <h2 className="font-bold text-[#05223D]">Upload the filled template</h2>
              <p className="text-sm text-[#465B70] mt-1 mb-3">Drop the .xlsx here or browse from disk. You’ll see a summary once the import completes.</p>
              <div
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                className="rounded-2xl border-2 border-dashed border-[#0A4D8C33] bg-[#FDFBF7] p-8 text-center transition-colors hover:border-[#F25C05] hover:bg-[#F25C05]/5"
                data-testid="bulk-import-dropzone"
              >
                <Upload className="h-8 w-8 mx-auto text-[#0A4D8C]/60 mb-2" />
                <p className="text-sm text-[#465B70]">Drag & drop or click to browse</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="bulk-import-file-input"
                />
                <Button onClick={() => inputRef.current?.click()} disabled={busy} className="mt-3 h-10 rounded-xl bg-[#F25C05] hover:bg-[#D95204] text-white" data-testid="bulk-import-browse-btn">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {busy ? 'Importing…' : 'Choose .xlsx file'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 — Result */}
      {result && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="bulk-import-result">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-emerald-800 font-bold mb-3"><CheckCircle2 size={18} /> Import complete</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat icon={Users} label="New customers" value={result.customers_created} accent="#F25C05" />
              <Stat icon={Users} label="Existing customers" value={result.customers_existing} accent="#7A8A99" />
              <Stat icon={FileSpreadsheet} label="Rows processed" value={result.rows_processed} accent="#0A4D8C" />
              <Stat icon={Mail} label="Welcome emails sent" value={result.emails_sent} accent="#F25C05" />
              <Stat icon={MessageSquare} label="WhatsApp queued" value={result.whatsapp_sent} accent="#25D366" />
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 p-3">
                <div className="flex items-center gap-2 text-rose-800 font-bold text-sm mb-2"><AlertCircle size={14} /> {result.errors.length} error(s):</div>
                <ul className="text-xs text-rose-800 space-y-1 list-disc list-inside">
                  {result.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>{e.email ? <><strong>{e.email}:</strong> </> : null}{e.error || e.row_error || JSON.stringify(e)}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 text-xs text-[#465B70]">
              Newly imported customers will appear under <a href="/admin/pending-customers" className="inline-flex items-center gap-1 font-bold text-[#0A4D8C] underline-offset-4 hover:underline">Waiting Confirmation <ExternalLink size={11} /></a> after they set their password.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      <Card className="border-[#0A4D8C1A]">
        <CardContent className="p-5">
          <h3 className="font-bold text-[#05223D] mb-2">Import rules</h3>
          <ul className="text-sm text-[#465B70] space-y-1.5 list-disc list-inside">
            <li>Required columns: <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">email</Badge> <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">name</Badge> <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">phone</Badge> <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">street</Badge> <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">product_slug</Badge> <Badge variant="secondary" className="rounded-full text-[10px] mx-0.5">qty</Badge></li>
            <li>Frequency values: <code>weekly</code>, <code>biweekly</code>, or <code>monthly</code>.</li>
            <li>Multiple products per customer: repeat the email on new rows; blank cells inherit from the first row of that customer.</li>
            <li>Duplicates (same email) are skipped for the user record; new subscriptions/products still merge onto the existing user.</li>
            <li>Each new customer receives a magic link valid for 7 days. Once they set their password they land in <em>pending_customer_confirmation</em>.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white border border-[#0A4D8C1A] p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#465B70] font-bold">
        <Icon size={12} style={{ color: accent }} />
        {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold text-[#05223D] tabular-nums">{value ?? 0}</div>
    </div>
  );
}
